import { useState } from 'react'

interface Port {
  port_number: number
  status: string
  is_up?: number | null
  speed?: number | null
  poe_power_draw?: number | null
  poe_enabled?: number | null
  connected_device_id?: string | null
  connected_device_label?: string | null
  connected_device_name?: string | null
  connected_device_mac?: string | null
  connected_device_ip?: string | null
  connected_spec_category?: string | null
}

interface Props {
  totalPorts: number
  ports: Port[]
  onPortClick?: (port: Port) => void
}

function getPortColor(port: Port): string {
  const isUp = port.is_up === 1 || port.status === 'connected' || port.status === 'uplink'
  // Red = error state
  if (port.status === 'error') return 'var(--err)'
  // Grey = disabled/down
  if (!isUp) {
    if (port.status === 'disabled') return 'var(--text-3)'
    return 'var(--border-default)' // dim for down ports
  }
  // Orange = PoE delivering power
  if ((port.poe_power_draw || 0) > 0 || port.poe_enabled === 1) return 'var(--warn)'
  // Green = active/linked
  return 'var(--ok)'
}

export function PortMapVisual({ totalPorts, ports, onPortClick }: Props) {
  const [hoveredPort, setHoveredPort] = useState<number | null>(null)

  const portMap = new Map<number, Port>()
  for (const p of ports) {
    if (p.port_number) portMap.set(p.port_number, p)
  }

  const cols = Math.min(12, Math.ceil(totalPorts / 2))
  const topRow = Array.from({ length: cols }, (_, i) => i + 1)
  const bottomRow = Array.from({ length: cols }, (_, i) => i + cols + 1).filter(n => n <= totalPorts)

  const renderPort = (portNum: number) => {
    const port = portMap.get(portNum)
    const isUp = port?.is_up === 1 || port?.status === 'connected' || port?.status === 'uplink'
    const color = port ? getPortColor(port) : 'var(--border-default)'
    const isHovered = hoveredPort === portNum
    const label = port?.connected_device_label || port?.connected_device_name || null
    const hasPoe = (port?.poe_power_draw || 0) > 0

    return (
      <div
        key={portNum}
        style={{
          position: 'relative',
          width: 20, height: 16,
          background: color,
          borderRadius: 2,
          border: isHovered ? '1.5px solid var(--text)' : '1px solid var(--border-subtle)',
          cursor: port ? 'pointer' : 'default',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 7, fontWeight: 600, fontFamily: 'var(--font-mono)',
          color: isUp ? '#fff' : 'var(--text-tertiary)',
          opacity: isUp ? 1 : 0.5,
          transition: 'transform 0.1s',
          transform: isHovered ? 'scale(1.2)' : 'scale(1)',
        }}
        onMouseEnter={() => setHoveredPort(portNum)}
        onMouseLeave={() => setHoveredPort(null)}
        onClick={() => port && onPortClick?.(port)}
      >
        {portNum}
        {isHovered && (label || port?.connected_device_mac) && (
          <div style={{
            position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
            background: 'var(--text)', color: '#fff', padding: '3px 6px',
            borderRadius: 4, fontSize: 10, whiteSpace: 'nowrap', zIndex: 10,
            marginBottom: 4, pointerEvents: 'none',
          }}>
            {label || port?.connected_device_mac}
            {port?.connected_device_ip && <span style={{ opacity: 0.7 }}> · {port.connected_device_ip}</span>}
            {hasPoe && <span style={{ opacity: 0.7 }}> · {port!.poe_power_draw!.toFixed(1)}W</span>}
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 2, marginBottom: 2 }}>
        {topRow.map(renderPort)}
      </div>
      <div style={{ display: 'flex', gap: 2 }}>
        {bottomRow.map(renderPort)}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 6, fontSize: 9, color: 'var(--text-tertiary)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ok)', display: 'inline-block' }} />
          Active
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--warn)', display: 'inline-block' }} />
          PoE
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--text-3)', display: 'inline-block' }} />
          Disabled
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--err)', display: 'inline-block' }} />
          Error
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--border-default)', display: 'inline-block', border: '1px solid var(--border-default)' }} />
          Down
        </span>
      </div>
    </div>
  )
}
